/* eslint-disable react/react-in-jsx-scope */
import ImpersonationHandler from "@/auth/components/ImpersonationHandler";
import RegistrationForm from "@/auth/components/RegistrationForm";
import ReverificationForm from "@/auth/components/ReverificationForm";
import { useAuth } from "@/auth/hooks/useAuth";
import { isPureFunder } from "@/auth/roleAccess";
import AuthenticationRequired from "@/components/AuthenticationRequired";
import NotFoundPage from "@/components/errors/404";
import ErrorBoundaryWrapper from "@/components/errors/ErrorBoundaryWrapper";
import LayoutDashboard from "@/dashboard/components/layout/Layout";
import Auth from "@/pages/auth/Auth";
import EmailVerification from "@/pages/auth/EmailVerification";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import Registration from "@/pages/auth/Registration";
import ResetPassword from "@/pages/auth/ResetPassword";
import AddCerfa from "@/pages/dashboard/AddCerfa";
import AddFunder from "@/pages/dashboard/AddFunder";
import CallForProjects from "@/pages/dashboard/CallForProjects";
import CerfaFormulaire from "@/pages/dashboard/CerfaFormulaire";
import Certificats from "@/pages/dashboard/Certificats";
import Dashboard from "@/pages/dashboard/Dashboard";
import DashboardFunder from "@/pages/dashboard/Funder";
import Funders from "@/pages/dashboard/Funders";
import Funding from "@/pages/dashboard/Funding";
import PermissionSubContractor from "@/pages/dashboard/PermissionSubcontractor";
import DashboardProgram from "@/pages/dashboard/Program";
import DashboardPrograms from "@/pages/dashboard/Programs";
import DashboardProject from "@/pages/dashboard/Project";
import CustomProjectForm from "@/pages/dashboard/CustomProjectForm";
import SubContractors from "@/pages/dashboard/Subcontractors";
import SynthesesFinanceurs from "@/pages/dashboard/syntheses/SynthesesFinanceurs";
import SynthesesIntervenants from "@/pages/dashboard/syntheses/SynthesesIntervenants";
import SynthesesProjets from "@/pages/dashboard/syntheses/SynthesesProjets";
import SynthesesTableauDeBord from "@/pages/dashboard/syntheses/SynthesesTableauDeBord";
import Unsubscribe from "@/pages/dashboard/Unsubscribe";
import UserManagement from "@/pages/dashboard/UserManagement";
import Documents from "@/pages/document/Documents";
import ParametersPage from "@/pages/parameters/ParametersPage";
import Preference from "@/pages/preference/preference";
import Home from "@/pages/submit/Home";
import LoginCall from "@/pages/submit/LoginCall";
import CallForProjectsEntry from "@/pages/submit/CallForProjectsEntry";
import CallForProjectsProjectSheet from "@/pages/submit/CallForProjectsProjectSheet";
import Program from "@/pages/submit/Program";
import Programs from "@/pages/submit/Programs";
import SubscriptionForMeta from "@/pages/submit/Subscription";
import SubscriptionPartner from "@/pages/submit/SubscriptionPartner";
import Layout from "@/programs/components/layout/Layout";
import LayoutSubmitCallForProjects from "@/programs/components/layout/LayoutSubmitCallForProjects";
import LayoutSubmit from "@/programs/components/layout/LayoutSubmit";
import { isFeatureEnabledForEmail } from "@/libs/featureAccess";
import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

const AppRoutes = () => {
  const { loading, clearSession, user } = useAuth();
  // const isSubcontractorSession = !!(
  //   localStorage.getItem("isSubcontractor") === "subcontractor"
  //   || sessionStorage.getItem("subcontractorId")
  // );
  const isSubmitCallForProjectsUser = !!(
    user?.roles?.includes("ROLE_CPINTERVENANT")
    // || user?.roles?.includes("ROLE_SUBCONTRACTOR")
  );
  const canAccessSubmitCallForProjects =
    isSubmitCallForProjectsUser; /*|| isSubcontractorSession;*/
  const location = useLocation();
  const hasPendingImpersonation = new URLSearchParams(location.search).has(
    "impersonation",
  );
  const [isAllowed, setIsAllowed] = useState<boolean>(true);
  const isPublicSubmitAccess =
    location.pathname.includes("/submit/user-partner-subscription/")
    || location.pathname.includes("/submit/subcontractor/")
    || location.pathname.includes("/submit/authentification-cfp/");
  const isFinancingAllowed = isFeatureEnabledForEmail(
    user?.email,
    import.meta.env.VITE_ALLOWED_FINANCING_USERS,
  );
  const isSynthesesAllowed = isFeatureEnabledForEmail(
    user?.email,
    import.meta.env.VITE_ALLOWED_SYNTHESES_USERS,
  );

  useEffect(() => {
    if (hasPendingImpersonation) {
      return;
    }

    if (loading) {
      return;
    }

    if (isPureFunder(user) && location.pathname !== "/login") {
      clearSession();
      return;
    }

    if (isPublicSubmitAccess) {
      setIsAllowed(isFinancingAllowed);
      return;
    }

    if (user?.status === 2) {
      localStorage.removeItem("jwtToken");
      localStorage.removeItem("refreshToken");
      sessionStorage.removeItem("jwtToken");
      sessionStorage.removeItem("refreshToken");
      window.location.href = "/login";
    } else if (
      (user?.status === 0 || user?.status === null) &&
      location.pathname.includes("dashboard")
    ) {
      window.location.href = "/submit";
    } else if (
      user?.status === 1
      && location.pathname.includes("submit")
      && !canAccessSubmitCallForProjects
    ) {
      window.location.href = "/dashboard";
    }

    setIsAllowed(isFinancingAllowed);
  }, [
    hasPendingImpersonation,
    canAccessSubmitCallForProjects,
    isFinancingAllowed,
    isPublicSubmitAccess,
    location.pathname,
    loading,
    clearSession,
    user,
  ]);

  const submitCallForProjectsLoadingElement = (
    <ErrorBoundaryWrapper>
      <LayoutSubmitCallForProjects>
        <div className="rounded-2xl border border-[#e1e8dc] bg-white p-6 text-sm text-[#5f6d5c]">
          Chargement...
        </div>
      </LayoutSubmitCallForProjects>
    </ErrorBoundaryWrapper>
  );

  return (
    <>
      <ImpersonationHandler />
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />

        <Route
          path="/access-code"
          element={
            <ErrorBoundaryWrapper>
              <Layout>
                <Home />
              </Layout>
            </ErrorBoundaryWrapper>
          }
        />

        {/* Route : /submit */}
        <Route path="/submit">
          <Route
            index
            element={
              !loading && canAccessSubmitCallForProjects ? (
                <Navigate to="/submit/appels-a-projets" replace />
              ) : (
                <ErrorBoundaryWrapper>
                  <LayoutSubmit>
                    <Home />
                  </LayoutSubmit>
                </ErrorBoundaryWrapper>
              )
            }
          />
          <Route
            path="program/:id"
            element={
              !loading && canAccessSubmitCallForProjects ? (
                <Navigate to="/submit/appels-a-projets" replace />
              ) : (
                <ErrorBoundaryWrapper>
                  <LayoutSubmit>
                    <Program />
                  </LayoutSubmit>
                </ErrorBoundaryWrapper>
              )
            }
          />
          <Route
            path="programs"
            element={
              !loading && canAccessSubmitCallForProjects ? (
                <Navigate to="/submit/appels-a-projets" replace />
              ) : (
                <ErrorBoundaryWrapper>
                  <LayoutSubmit>
                    <Programs />
                  </LayoutSubmit>
                </ErrorBoundaryWrapper>
              )
            }
          />
          <Route
            path="appels-a-projets"
            element={
              loading ? (
                submitCallForProjectsLoadingElement
              ) : canAccessSubmitCallForProjects ? (
                <ErrorBoundaryWrapper>
                  <LayoutSubmitCallForProjects>
                    <CallForProjectsEntry />
                  </LayoutSubmitCallForProjects>
                </ErrorBoundaryWrapper>
              ) : (
                <Navigate to="/submit/programs" replace />
              )
            }
          />
          <Route
            path="appels-a-projets/nouveau"
            element={
              loading ? (
                submitCallForProjectsLoadingElement
              ) : canAccessSubmitCallForProjects ? (
                <ErrorBoundaryWrapper>
                  <LayoutSubmitCallForProjects>
                    <CallForProjectsEntry />
                  </LayoutSubmitCallForProjects>
                </ErrorBoundaryWrapper>
              ) : (
                <Navigate to="/submit/programs" replace />
              )
            }
          />
          <Route
            path="appels-a-projets/projet/:projectId"
            element={
              loading ? (
                submitCallForProjectsLoadingElement
              ) : canAccessSubmitCallForProjects ? (
                <ErrorBoundaryWrapper>
                  <LayoutSubmitCallForProjects>
                    <CallForProjectsProjectSheet />
                  </LayoutSubmitCallForProjects>
                </ErrorBoundaryWrapper>
              ) : (
                <Navigate to="/submit/programs" replace />
              )
            }
          />
          <Route
            path="user-subscription/:metacode"
            element={
              <ErrorBoundaryWrapper>
                <SubscriptionForMeta />
              </ErrorBoundaryWrapper>
            }
          />
          <Route
            path="cfp-subscription/:metacode/:callId"
            element={
              <ErrorBoundaryWrapper>
                <SubscriptionForMeta />
              </ErrorBoundaryWrapper>
            }
          />
          <Route
            path="user-partner-subscription/:metacode"
            element={
              <ErrorBoundaryWrapper>
                <SubscriptionPartner />
              </ErrorBoundaryWrapper>
            }
          />
        </Route>

        <Route
          path="/login"
          element={
            <ErrorBoundaryWrapper>
              <Layout>
                <Auth />
              </Layout>
            </ErrorBoundaryWrapper>
          }
        />
        <Route
          path="/registration"
          element={
            <ErrorBoundaryWrapper>
              <Layout>
                <Registration />
              </Layout>
            </ErrorBoundaryWrapper>
          }
        />
        <Route
          path="/sign-up"
          element={
            <ErrorBoundaryWrapper>
              <Layout>
                <RegistrationForm />
              </Layout>
            </ErrorBoundaryWrapper>
          }
        />
        <Route
          path="/authentification-cfp/:link/:slugName"
          element={
            <ErrorBoundaryWrapper>
              <LoginCall />
            </ErrorBoundaryWrapper>
          }
        />
        <Route
          path="/register/verify"
          element={
            <ErrorBoundaryWrapper>
              <Layout>
                <EmailVerification />
              </Layout>
            </ErrorBoundaryWrapper>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <ErrorBoundaryWrapper>
              <Layout>
                <ForgotPassword />
              </Layout>
            </ErrorBoundaryWrapper>
          }
        />
        <Route
          path="/reset-password"
          element={
            <ErrorBoundaryWrapper>
              <Layout>
                <ResetPassword />
              </Layout>
            </ErrorBoundaryWrapper>
          }
        />
        <Route
          path="/verify-error"
          element={
            <ErrorBoundaryWrapper>
              <Layout>
                <ReverificationForm />
              </Layout>
            </ErrorBoundaryWrapper>
          }
        />

        {/* Route : /dashboard */}
        <Route path="/dashboard">
          <Route
            index
            element={
              <ErrorBoundaryWrapper>
                <AuthenticationRequired redirect="dashboard">
                  <LayoutDashboard>
                    <Dashboard />
                  </LayoutDashboard>
                </AuthenticationRequired>
              </ErrorBoundaryWrapper>
            }
          />

          {isAllowed && (
            <>
              <Route
                path="mes-financeurs"
                element={
                  <ErrorBoundaryWrapper>
                    <AuthenticationRequired redirect="dashboard">
                      <LayoutDashboard showButtons={false}>
                        <Funders />
                      </LayoutDashboard>
                    </AuthenticationRequired>
                  </ErrorBoundaryWrapper>
                }
              />
              <Route
                path="financeur/:id"
                element={
                  <ErrorBoundaryWrapper>
                    <AuthenticationRequired redirect="dashboard">
                      <LayoutDashboard showButtons={false}>
                        <DashboardFunder />
                      </LayoutDashboard>
                    </AuthenticationRequired>
                  </ErrorBoundaryWrapper>
                }
              />
              <Route
                path="suivi-financement"
                element={
                  <ErrorBoundaryWrapper>
                    <AuthenticationRequired redirect="dashboard">
                      <LayoutDashboard showButtons={false}>
                        <Funding />
                      </LayoutDashboard>
                    </AuthenticationRequired>
                  </ErrorBoundaryWrapper>
                }
              />
              <Route
                path="ajout-financeur"
                element={
                  <ErrorBoundaryWrapper>
                    <AuthenticationRequired redirect="dashboard">
                      <LayoutDashboard showButtons={false}>
                        <AddFunder />
                      </LayoutDashboard>
                    </AuthenticationRequired>
                  </ErrorBoundaryWrapper>
                }
              />
            </>
          )}

          {isSynthesesAllowed && (
            <>
              <Route
                path="syntheses"
                element={<Navigate to="/dashboard/syntheses/tableau-de-bord" replace />}
              />
              <Route
                path="syntheses/tableau-de-bord"
                element={
                  <ErrorBoundaryWrapper>
                    <AuthenticationRequired redirect="dashboard">
                      <LayoutDashboard showButtons={false}>
                        <SynthesesTableauDeBord />
                      </LayoutDashboard>
                    </AuthenticationRequired>
                  </ErrorBoundaryWrapper>
                }
              />
              <Route
                path="syntheses/financeurs"
                element={
                  <ErrorBoundaryWrapper>
                    <AuthenticationRequired redirect="dashboard">
                      <LayoutDashboard showButtons={false}>
                        <SynthesesFinanceurs />
                      </LayoutDashboard>
                    </AuthenticationRequired>
                  </ErrorBoundaryWrapper>
                }
              />
              <Route
                path="syntheses/intervenants"
                element={
                  <ErrorBoundaryWrapper>
                    <AuthenticationRequired redirect="dashboard">
                      <LayoutDashboard showButtons={false}>
                        <SynthesesIntervenants />
                      </LayoutDashboard>
                    </AuthenticationRequired>
                  </ErrorBoundaryWrapper>
                }
              />
              <Route
                path="syntheses/projets"
                element={
                  <ErrorBoundaryWrapper>
                    <AuthenticationRequired redirect="dashboard">
                      <LayoutDashboard showButtons={false}>
                        <SynthesesProjets />
                      </LayoutDashboard>
                    </AuthenticationRequired>
                  </ErrorBoundaryWrapper>
                }
              />
            </>
          )}

          <Route
            path="programs"
            element={
              <ErrorBoundaryWrapper>
                <AuthenticationRequired redirect="dashboard">
                  <LayoutDashboard>
                    <DashboardPrograms />
                  </LayoutDashboard>
                </AuthenticationRequired>
              </ErrorBoundaryWrapper>
            }
          />
          <Route
            path="program/:id"
            element={
              <ErrorBoundaryWrapper>
                <AuthenticationRequired redirect="dashboard">
                  <LayoutDashboard>
                    <DashboardProgram />
                  </LayoutDashboard>
                </AuthenticationRequired>
              </ErrorBoundaryWrapper>
            }
          />
          <Route
            path="mes-documents"
            element={
              <ErrorBoundaryWrapper>
                <AuthenticationRequired redirect="/dashboard">
                  <LayoutDashboard>
                    <Documents />
                  </LayoutDashboard>
                </AuthenticationRequired>
              </ErrorBoundaryWrapper>
            }
          />
          <Route
            path="project/:id"
            element={
              <ErrorBoundaryWrapper>
                <AuthenticationRequired redirect="dashboard">
                  <LayoutDashboard>
                    <DashboardProject />
                  </LayoutDashboard>
                </AuthenticationRequired>
              </ErrorBoundaryWrapper>
            }
          />
          <Route
            path="project/create-custom"
            element={
              <ErrorBoundaryWrapper>
                <AuthenticationRequired redirect="dashboard">
                  <LayoutDashboard>
                    <CustomProjectForm />
                  </LayoutDashboard>
                </AuthenticationRequired>
              </ErrorBoundaryWrapper>
            }
          />
          <Route
            path="preference/:module"
            element={
              <ErrorBoundaryWrapper>
                <AuthenticationRequired redirect="dashboard">
                  <LayoutDashboard>
                    <Preference />
                  </LayoutDashboard>
                </AuthenticationRequired>
              </ErrorBoundaryWrapper>
            }
          />
          <Route
            path="subcontractors"
            element={
              <ErrorBoundaryWrapper>
                <AuthenticationRequired redirect="dashboard">
                  <LayoutDashboard>
                    <SubContractors />
                  </LayoutDashboard>
                </AuthenticationRequired>
              </ErrorBoundaryWrapper>
            }
          />
          <Route
            path="call-for-projects"
            element={
              <ErrorBoundaryWrapper>
                <AuthenticationRequired redirect="dashboard">
                  <LayoutDashboard>
                    <CallForProjects />
                  </LayoutDashboard>
                </AuthenticationRequired>
              </ErrorBoundaryWrapper>
            }
          />
          <Route
            path="subcontractor/permission/:id"
            element={
              <ErrorBoundaryWrapper>
                <AuthenticationRequired redirect="dashboard">
                  <LayoutDashboard>
                    <PermissionSubContractor />
                  </LayoutDashboard>
                </AuthenticationRequired>
              </ErrorBoundaryWrapper>
            }
          />
          <Route
            path="certificats"
            element={
              <ErrorBoundaryWrapper>
                <AuthenticationRequired redirect="dashboard">
                  <LayoutDashboard>
                    <Certificats />
                  </LayoutDashboard>
                </AuthenticationRequired>
              </ErrorBoundaryWrapper>
            }
          />
          <Route
            path="formulaire-cerfa"
            element={
              <ErrorBoundaryWrapper>
                <AuthenticationRequired redirect="dashboard">
                  <LayoutDashboard>
                    <CerfaFormulaire />
                  </LayoutDashboard>
                </AuthenticationRequired>
              </ErrorBoundaryWrapper>
            }
          />
          <Route
            path="add-cerfa"
            element={
              <ErrorBoundaryWrapper>
                <AuthenticationRequired redirect="dashboard">
                  <LayoutDashboard>
                    <AddCerfa />
                  </LayoutDashboard>
                </AuthenticationRequired>
              </ErrorBoundaryWrapper>
            }
          />
          <Route
            path="parameters"
            element={
              <ErrorBoundaryWrapper>
                <AuthenticationRequired redirect="dashboard">
                  <LayoutDashboard>
                    <ParametersPage/>
                    {/* <ParameterIndicator /> */}
                  </LayoutDashboard>
                </AuthenticationRequired>
              </ErrorBoundaryWrapper>
            }
          />
       
          <Route
            path="user-management"
            element={
              <ErrorBoundaryWrapper>
                <AuthenticationRequired redirect="dashboard">
                  <LayoutDashboard>
                    <UserManagement />
                  </LayoutDashboard>
                </AuthenticationRequired>
              </ErrorBoundaryWrapper>
            }
          />
        </Route>

        <Route
          path="unsubscribe/:uid"
          element={
            <ErrorBoundaryWrapper>
              <Unsubscribe />
            </ErrorBoundaryWrapper>
          }
        />
        <Route
          path="*"
          element={
            <ErrorBoundaryWrapper>
              <Layout>
                <NotFoundPage isPageChildren={false} />
              </Layout>
            </ErrorBoundaryWrapper>
          }
        />
      </Routes>
    </>
  );
};

export default AppRoutes;
